import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { PIKKO_JACKPOT_DISPLAY_TYPE } from '../../../constants/pikkoJackpotConstants';
import { PikkoJackpotHasAppCard } from './PikkoJackpotHasAppCard';
import { PikkoJackpotLastChanceCard } from './PikkoJackpotLastChanceCard';
import { PikkoJackpotReminderCard } from './PikkoJackpotReminderCard';

// ── Card lookup by display type ──────────────────────────
const CARD_BY_TYPE = {
  [PIKKO_JACKPOT_DISPLAY_TYPE.LAST_CHANCE]: PikkoJackpotLastChanceCard,
  [PIKKO_JACKPOT_DISPLAY_TYPE.REMINDER]: PikkoJackpotReminderCard,
};

// ── Transition timing ────────────────────────────────────
const EXIT_DURATION = 400; // fade-out before swapping card
const ENTER_DELAY = 150; // pause before fade-in of new card

/**
 * JackpotCard — renders the right card based on `offer.displayType`.
 *
 * When the displayType changes (e.g. LAST_CHANCE → REMINDER after reactivation),
 * the component animates a smooth 3-phase transition:
 *   1. "exiting"  → current card fades out & shrinks
 *   2. "entering" → new card is mounted (still invisible briefly)
 *   3. "visible"  → new card fades in
 *
 * If the user already has the app (HAS_NEW_APP_CREDITED / HAS_APP_EXISTING),
 * HasAppCard is rendered directly without transition logic.
 */
export const JackpotCard = () => {
  const { offer } = useSelector((state) => state.jackpot);
  const displayType = offer?.displayType || PIKKO_JACKPOT_DISPLAY_TYPE.NONE;

  const isAppUser =
    displayType === PIKKO_JACKPOT_DISPLAY_TYPE.HAS_NEW_APP_CREDITED ||
    displayType === PIKKO_JACKPOT_DISPLAY_TYPE.HAS_APP_EXISTING;

  const offerActive = offer?.isActive;
  const userOfferBalance = offer?.balance;

  if (isAppUser && offerActive && userOfferBalance > 0) {
    return <PikkoJackpotHasAppCard offer={offer} />;
  }

  return <AnimatedCardSwitch displayType={displayType} offer={offer} />;
};

// ── Animated card transition ─────────────────────────────

const AnimatedCardSwitch = ({ displayType, offer }) => {
  const prevTypeRef = useRef(displayType);
  const [activeType, setActiveType] = useState(displayType);
  const [phase, setPhase] = useState('visible'); // 'exiting' | 'entering' | 'visible'

  useEffect(() => {
    if (displayType === prevTypeRef.current) return;
    prevTypeRef.current = displayType;

    // Phase 1 — fade out old card
    setPhase('exiting');

    const exitTimer = setTimeout(() => {
      // Phase 2 — swap to new card
      setActiveType(displayType);
      setPhase('entering');

      // Phase 3 — fade in new card
      const enterTimer = setTimeout(() => setPhase('visible'), ENTER_DELAY);
      return () => clearTimeout(enterTimer);
    }, EXIT_DURATION);

    return () => clearTimeout(exitTimer);
  }, [displayType]);

  const CardComponent = CARD_BY_TYPE[activeType];
  if (!CardComponent) return null;

  const style = {
    transition: `opacity ${EXIT_DURATION}ms ease, transform ${EXIT_DURATION}ms ease`,
    opacity: phase === 'exiting' ? 0 : 1,
    transform: phase === 'exiting' ? 'scale(0.95) translateY(12px)' : 'none',
  };

  return (
    <div style={style}>
      <CardComponent offer={offer} />
    </div>
  );
};
